import { TimeAndBudgetPage_Site } from '__generated__/TimeAndBudgetPage_Site';
import { TimeAndBudgetPage_StructuredBrief } from '__generated__/TimeAndBudgetPage_StructuredBrief';
import { PreziTimeframe } from '__generated__/types';
import Responsive from 'common/components/Responsive';
import { handlePromise, onReject } from 'common/utils/Bugsnag';
import ColorComponent from 'projects/components/Color';
import DateRange from 'projects/components/DateRange';
import FadeIn from 'projects/components/FadeIn';
import {
    ObservableField as Field,
    ObservableForm,
    Observe,
} from 'projects/components/ObservableForm';
import { Color } from 'projects/consts';
import { trackBudgetEvent, trackViewedBriefEvent } from 'projects/events';
import React, { useEffect } from 'react';
import TimeFrame from './TimeFrame';

type GetFunctionProps<T> = T extends (props: infer U) => any ? U : never;
type trackBudgetEventParams = GetFunctionProps<typeof trackBudgetEvent>;
type inputType = trackBudgetEventParams['input_type'];

interface Fields {
    timeframe: PreziTimeframe;
    startDate: any | null;
    endDate: any | null;
}

export interface Props<T> {
    form: ObservableForm<T>;
    brief: TimeAndBudgetPage_StructuredBrief;
    site: TimeAndBudgetPage_Site;
}

const TimeAndBudgetPage: <T extends Fields>(props: Props<T>) => React.ReactElement<Props<T>> = ({
    form,
    brief,
}) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'TimeAndBudget',
        });
    }, []);

    const timeframe = () => {
        return form.get('timeframe').toUpperCase();
    };

    const renderChooseDates = (title: string) => {
        const dateField = (
            <Field
                form={form}
                name={['startDate', 'endDate']}
                title={title}
                render={() => (
                    <>
                        <Responsive min="normal">
                            <DateRange
                                startDate={form.get('startDate')}
                                endDate={form.get('endDate')}
                                onChange={(name, value) => {
                                    handlePromise(form.set(name, value), 'TimeAndBudgetPage');
                                }}
                                orientation="horizontal"
                            />
                        </Responsive>
                        <Responsive max="medium">
                            <DateRange
                                startDate={form.get('startDate')}
                                endDate={form.get('endDate')}
                                onChange={(name, value) => {
                                    handlePromise(form.set(name, value), 'TimeAndBudgetPage');
                                }}
                                orientation="vertical"
                            />
                        </Responsive>
                    </>
                )}
            />
        );

        return (
            <FadeIn wrapperComponent="div" wrapperClassName="wrapped-card">
                {timeframe() == PreziTimeframe.SPECIFIC_DATES && dateField}
            </FadeIn>
        );
    };

    return (
        <div className="content-setting">
            <Field
                form={form}
                name="timeframe"
                title={__("What's your timeline?")}
                message={__(
                    'Your designer will confirm whether your delivery date can be met based on project scope and their availability.'
                )}
                render={() => (
                    <ColorComponent color={Color.createBlue}>
                        <TimeFrame
                            value={timeframe()}
                            onChange={async value => {
                                try {
                                    await form.set('timeframe', value);
                                } catch {
                                    onReject('TimeAndBudgetPage');
                                } finally {
                                    if (value === PreziTimeframe.SPECIFIC_DATES) {
                                        form.scrollTo('startDate');
                                    }
                                }
                            }}
                        />
                    </ColorComponent>
                )}
            />

            <Observe
                form={form}
                name="timeframe"
                render={() => renderChooseDates(__('Choose a start & end date'))}
            />
        </div>
    );
};

export default TimeAndBudgetPage;
